import React, { useEffect, useState, useRef } from "react";
import { observer } from "mobx-react";
import { FilterComponent } from "./filterComponent/FilterComponent";
import ContactSearchTable from "./contactTable/ContactSearchTable";
import CompanySearchTable from "./contactTable/CompanySearchTable";
import { dataSearchState, decodeListBuilderExtensionImportParam } from "./state";
import customListState from "./CustomList/state";
import CreateEditListPopup from "./CustomList/createEditListPopup";
import PopupLoader from "./PopupLoader";
import InputTextBox from "Components/common/InputTextBox";
import Button from "Components/common/Button";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import Utils from "Utils/utils";
import { MXP_EVENT, PAGE_TYPE } from "Static/MixPanelEvents";

const DataSearch = observer(props => {
  const [tableLoading, setTableLoading] = useState(false);
  const [showSavePopup, setShowSavePopup] = useState(false);
  const [editPopup, setEditPopup] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchId, setSearchId] = useState("");
  const [errorMessage, setErrorMessage] = useState(false);
  const [savedListOptions, setSavedListOptions] = useState([]);
  const [filterSliderOpen, setFilterSliderOpen] = useState(false);
  const inputBoxRef = useRef();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sgLb = params.get("sgLb");
    if (sgLb) {
      const parsed = decodeListBuilderExtensionImportParam(sgLb);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("sgLb");
        window.history.replaceState({}, "", url.pathname + url.search + url.hash);
      } catch (e) {
        /* ignore */
      }
      if (parsed && parsed.filters) {
        dataSearchState.applyFiltersFromCompanyExtensionImport(parsed.filters);
      }
    } else {
      dataSearchState.loadPersistedFilters();
    }
    return () => {
      dataSearchState.setContactTableData({});
      dataSearchState.setRangeSelected("");
      dataSearchState.setShowInitialPage(true);
      customListState.setActiveList("");
    };
  }, []);

  // Sync discoverTableLoading (e.g. from auto-search on first filter) so table shows loading state
  useEffect(() => {
    setTableLoading(dataSearchState.discoverTableLoading);
  }, [dataSearchState.discoverTableLoading]);

  useEffect(() => {
    saveSearchConfirmationPopup();
  }, [editPopup]);

  const saveSearch = (savedpayload = {}) => {
    const { companyDomainArr = [], locationArr = [], companyLocationArr = [] } = savedpayload || {};
    let companies = [];
    if (dataSearchState?.selectedFilteredList?.companyDomain) {
      for (const iterator of dataSearchState?.selectedFilteredList
        ?.companyDomain) {
        let { id = "", name = "", primaryDomain = "" } = iterator;
        companies.push({ id, name, primaryDomain });
      }
    }
    let locations = [];
    if (dataSearchState?.selectedFilteredList?.location) {
      for (const iterator of dataSearchState?.selectedFilteredList?.location) {
        let { id = "", name = "", group = "", searchText = [] } = iterator;
        locations.push({ id, name, group, searchText });
      }
    }
    let companyLocations = [];
    if (dataSearchState?.selectedFilteredList?.companyLocation) {
      for (const iterator of dataSearchState?.selectedFilteredList?.companyLocation) {
        let { id = "", name = "", group = "", searchText = [] } = iterator;
        companyLocations.push({ id, name, group, searchText });
      }
    }

    const config = {
      url: URLS.saveSearchV2,
      method: "POST",
      data: {
        searchName: searchInput,
        ...dataSearchState.filterComponentData,
        locationIds: locationArr,
        companyId: companyDomainArr,
        companyLocationIds: companyLocationArr,
        locations,
        companies,
        companyLocations
      }
    };
    return makeApi(config);
  };

  const locationAndCompanyDomainPayloadCreation = () => {
    const { companyDomain = [], location = [], companyLocation = [] } =
      dataSearchState?.selectedFilteredList || {};
    let companyDomainArr = [],
      locationArr = [],
      companyLocationArr = [];
    if (companyDomain && companyDomain?.length > 0) {
      for (const iterator of companyDomain) {
        companyDomainArr.push(iterator?.id);
      }
    }
    if (location && location?.length > 0) {
      for (const iterator of location) {
        locationArr.push(iterator?.id);
      }
    }
    if (companyLocation && companyLocation?.length > 0) {
      for (const iterator of companyLocation) {
        companyLocationArr.push(iterator?.id);
      }
    }
    return { companyDomainArr, locationArr, companyLocationArr };
  };

  const handleSavedSearch = async event => {
    setSaveLoader(true);
    let savedpayload = locationAndCompanyDomainPayloadCreation();
    let response = await saveSearch(savedpayload);
    if (response?.status === 200) {
      Utils.mixpanelTrack(MXP_EVENT.SAVE_SEARCH, {
        pageType: PAGE_TYPE.discover
      });
      toasterState.setToastMsg("Search saved succesfully", "success");
    } else {
      toasterState.setToastMsg(
        "save operation was unsuccessful,please try again later",
        "failure"
      );
    }
    setSaveLoader(false);
    setShowSavePopup(false);
  };

  const handleInputBoxEvent = event => {
    Utils.preventDefaultFn(event);
    if (Utils.isSubmitKeyAction(event)) {
      handleSavedSearch();
    }
  };

  const minimumSelectionMade =
    (dataSearchState?.selectedFilteredList &&
      Object.keys(dataSearchState?.selectedFilteredList).length === 0 &&
      dataSearchState?.filterComponentData &&
      Object.keys(dataSearchState?.filterComponentData).length <= 2) ||
    false;

  const inputProps = {
    name: showSavePopup ? "Save Search" : "Edit Search",
    id: showSavePopup ? "saveSearch" : "editsearch",
    placeHolder: showSavePopup ? "CMOs of $10M+ companies" : "Edit Search",
    type: "text",
    value: searchInput,
    change: (id, val) => {
      setErrorMessage("");
      setSearchInput(val);
    },
    cbk: e => {
      if (!checkDuplicateName()) {
        if (searchInput) {
          showSavePopup ? handleInputBoxEvent(e) : saveSearchAfterEditText(e);
        }
      } else {
        if (Utils.isSubmitKeyAction(e)) {
          setErrorMessage("Entered search already exists");
        }
      }
    },
    err: errorMessage,
    readOnly: false,
    inputBoxRef: inputBoxRef,
    className: `${searchInput ? "activeBorder" : ""}`
  };

  const btnProps = {
    id: showSavePopup ? "Saved Search Button" : "Edit Search Button",
    name: showSavePopup ? "savedSearchButton" : "editSearchButton",
    btnText: showSavePopup ? "Save Search" : "Edit search",
    type: "button",
    className: "saveButton",
    className: `saveButton ${saveLoader ? "loading" : ""}`,
    loader: saveLoader,
    click: e => {
      if (!checkDuplicateName()) {
        if (searchInput) {
          setErrorMessage("");
          showSavePopup ? handleSavedSearch(e) : editSavedSearch();
        }
      } else {
        setErrorMessage("Entered search already exists");
      }
    },
    disabled: !searchInput || (minimumSelectionMade && !editPopup)
  };

  const getSavedSearch = () => {
    const config = {
      url: URLS.getSavedSearchV2,
      method: "GET"
    };
    return makeApi(config);
  };

  const initialGetSearch = async () => {
    let response = await getSavedSearch();
    if (response?.status === 200) {
      const raw = response?.data;
      const list = Array.isArray(raw)
        ? raw
        : raw?.content ?? raw?.data ?? raw?.savedSearches ?? [];
      setSavedListOptions(Array.isArray(list) ? list : []);
    }
  };

  const checkDuplicateName = () => {
    if (Array.isArray(savedListOptions)) {
      return savedListOptions?.some(item => {
        return item?.searchName === searchInput;
      });
    }
  };

  const editSavedSearch = async () => {
    let response = await editApiCall();
    setEditPopup(false);
    if (response?.status === 200) {
      Utils.mixpanelTrack(MXP_EVENT.EDIT_SEARCH, {
        pageType: PAGE_TYPE.discover
      });
      toasterState.setToastMsg("saved search edited successfully", "success");
    } else {
      toasterState.setToastMsg("saved search edit was unsuccessful", "failure");
    }
  };

  const editApiCall = () => {
    const config = {
      url: URLS.editSearchV2,
      method: "POST",
      data: { id: searchId, name: searchInput }
    };
    return makeApi(config);
  };

  const saveSearchAfterEditText = e => {
    Utils.preventDefaultFn(e);
    if (Utils.isSubmitKeyAction(e)) {
      editSavedSearch();
    }
  };

  const editCallBack = (id = "", name = "") => {
    setErrorMessage("");
    setSearchInput(name);
    setSearchId(id);
    setEditPopup(true);
  };

  useEffect(() => {
    if (showSavePopup) {
      setSearchInput("");
      setErrorMessage("");
    }
  }, [showSavePopup]);

  useEffect(() => {
    if (showSavePopup || editPopup) {
      initialGetSearch();
    }
  }, [showSavePopup, editPopup]);

  const saveSearchConfirmationPopup = () => {
    let displayText = editPopup ? "Edit Search" : "Save Search";
    return (
      <section className="createSavedSearchPopupWrapper">
        <div className="card">
          <div className="popupHeader">
            <div className="title">{displayText}</div>
            <i
              className="material-icons commonPopupCloseIcon"
              onClick={() =>
                editPopup ? setEditPopup(false) : setShowSavePopup(false)
              }
            >
              close
            </i>
          </div>
          <InputTextBox {...inputProps} />
          <Button {...btnProps} />
        </div>
        <div
          className="common-popup-mask"
          onClick={() => setShowSavePopup(false)}
        />
      </section>
    );
  };

  const activeTab = dataSearchState.activeSearchTab;
  const [tabFading, setTabFading] = useState(false);

  const switchTab = tab => {
    if (tab === activeTab || tabFading) return;
    setTabFading(true);
    setTimeout(() => {
      dataSearchState.setActiveSearchTab(tab);
      setTabFading(false);
    }, 130);
  };

  return (
    <section className={`dataSearchV2Wrapper ${filterSliderOpen ? "filterSliderOpen" : ""}`}>
      <div className="dataSearchV2Filter">
        <FilterComponent
          setTableLoading={setTableLoading}
          setShowSavePopup={setShowSavePopup}
          saveSearchConfirmationPopup={saveSearchConfirmationPopup}
          setEditPopup={setEditPopup}
          setSearchId={setSearchId}
          searchInput={searchInput}
          editCallBack={editCallBack}
          minimumSelectionMade={minimumSelectionMade}
          savedListOptions={savedListOptions}
          setSavedListOptions={setSavedListOptions}
          onSliderOpenChange={setFilterSliderOpen}
          activeTab={activeTab}
        />
      </div>

      {(showSavePopup || editPopup) && saveSearchConfirmationPopup()}

      <div className="dataSearchTable">
        <div className="dsTabBar">
          <button
            className={`dsTab ${activeTab === "contacts" ? "dsTab--active" : ""}`}
            onClick={() => switchTab("contacts")}
          >
            <i className="material-icons">people</i>
            Contacts
          </button>
          <button
            className={`dsTab ${activeTab === "companies" ? "dsTab--active" : ""}`}
            onClick={() => switchTab("companies")}
          >
            <i className="material-icons">business</i>
            Companies
          </button>
        </div>

        <div className={`dsTabContent ${tabFading ? "dsTabContent--fading" : ""}`}>
          {activeTab === "contacts" ? (
            <ContactSearchTable
              setTableLoading={setTableLoading}
              tableLoading={tableLoading}
            />
          ) : (
            <CompanySearchTable />
          )}
        </div>
      </div>
      <CreateEditListPopup />
      <PopupLoader />
    </section>
  );
});

export { DataSearch };
export default DataSearch;
